// First Created - 04 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board administration.

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'

/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionBoardsAdapter =
  createEntityAdapter({})

const initialState =
  orgIntentionBoardsAdapter
    .getInitialState()

/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionBoardsApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Intention Boards
        |--------------------------------------------------------------------------
        |
        | Optional orgId filter.
        |
        */

        getOrgIntentionBoards:
          builder.query({

            query:
              ({
                orgId,
              } = {}) => ({

                url:
                  '/orgIntentionBoard/getOrgIntentionBoards',

                params:
                  orgId
                    ? {
                        orgId,
                      }
                    : undefined,

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            transformResponse:
              responseData => {

                const loadedOrgIntentionBoards =
                  responseData.map(
                    intentionBoard => {

                      intentionBoard.id =
                        intentionBoard._id

                      return intentionBoard

                    }
                  )

                return orgIntentionBoardsAdapter
                  .setAll(
                    initialState,
                    loadedOrgIntentionBoards
                  )

              },

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                if (
                  result?.ids
                ) {

                  return [
                    {
                      type:
                        'OrgIntentionBoard',

                      id:
                        'LIST',
                    },

                    ...result.ids.map(
                      id => ({
                        type:
                          'OrgIntentionBoard',

                        id,
                      })
                    ),
                  ]

                }

                return [
                  {
                    type:
                      'OrgIntentionBoard',

                    id:
                      'LIST',
                  },
                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Intention Board By Id
        |--------------------------------------------------------------------------
        */

        getOrgIntentionBoardById:
          builder.query({

            query:
              ({
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionBoard/getOrgIntentionBoardById',

                params: {
                  intentionBoardId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionBoard',

                  id:
                    arg
                      ?.intentionBoardId,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Create Intention Board
        |--------------------------------------------------------------------------
        */

        createNewOrgIntentionBoard:
          builder.mutation({

            query:
              initialOrgIntentionBoard => ({

                url:
                  '/orgIntentionBoard/createNewOrgIntentionBoard',

                method:
                  'POST',

                body: {
                  ...initialOrgIntentionBoard,
                },

              }),

            invalidatesTags: [

              {
                type:
                  'OrgIntentionBoard',

                id:
                  'LIST',
              },

            ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Update Intention Board
        |--------------------------------------------------------------------------
        */

        updateOrgIntentionBoard:
          builder.mutation({

            query:
              initialOrgIntentionBoard => ({

                url:
                  '/orgIntentionBoard/updateOrgIntentionBoard',

                method:
                  'PATCH',

                body: {
                  ...initialOrgIntentionBoard,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionBoard',

                  id:
                    arg
                      .intentionBoardId,
                },

                {
                  type:
                    'OrgIntentionBoard',

                  id:
                    'LIST',
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Delete Intention Board
        |--------------------------------------------------------------------------
        */

        deleteOrgIntentionBoard:
          builder.mutation({

            query:
              ({
                intentionBoardId,
              }) => ({

                url:
                  '/orgIntentionBoard/deleteOrgIntentionBoard',

                method:
                  'DELETE',

                body: {
                  intentionBoardId,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionBoard',

                  id:
                    arg
                      .intentionBoardId,
                },

                {
                  type:
                    'OrgIntentionBoard',

                  id:
                    'LIST',
                },

              ],

          }),

      }),

  })

/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  useGetOrgIntentionBoardsQuery,

  useGetOrgIntentionBoardByIdQuery,

  useCreateNewOrgIntentionBoardMutation,

  useUpdateOrgIntentionBoardMutation,

  useDeleteOrgIntentionBoardMutation,

} =
  orgIntentionBoardsApiSlice

/*
|--------------------------------------------------------------------------
| Select Intention Board Query Result
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionBoardsResult =
  orgIntentionBoardsApiSlice
    .endpoints
    .getOrgIntentionBoards
    .select()

/*
|--------------------------------------------------------------------------
| Select Intention Board Data
|--------------------------------------------------------------------------
*/

const selectOrgIntentionBoardsData =
  createSelector(

    selectOrgIntentionBoardsResult,

    orgIntentionBoardsResult =>
      orgIntentionBoardsResult
        .data

  )

/*
|--------------------------------------------------------------------------
| Entity Selectors
|--------------------------------------------------------------------------
*/

export const {

  selectAll:
    selectAllOrgIntentionBoards,

  selectById:
    selectOrgIntentionBoardById,

  selectIds:
    selectOrgIntentionBoardIds,

} =
  orgIntentionBoardsAdapter
    .getSelectors(

      state =>
        selectOrgIntentionBoardsData(
          state
        ) ??
        initialState

    )